import { mdiDownload, mdiFilePdfBox, mdiOpenInNew } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import * as React from 'react';
import { IRemitClaim, IRemitLine, postedCount, remitBalance } from '../../services/remits/remit-claims';
import { IRemit } from '../../services/remits/remits-api';
import { RemitsStore } from '../../stores/remits/remits.store';
import { BaseButton } from '../base/button/button';
import { Notice } from '../base/notice/notice';
import { Spinner } from '../base/spinner/spinner';
import { day, money, paymentTypeLabel, plain, serviceDays, signedMoney } from './remit-format';
import { RemitLineDialog } from './remit-line-dialog';
import * as S from './remits.styles';

export interface IRemitDetailProps {
    store: RemitsStore;
    remit: IRemit;
    /** Opens the service-line popup in the console's own dialog host. */
    openLine: (line: IRemitLine, claim: IRemitClaim, patient?: string) => void;
    /** The claim's page on this console. */
    claimHref: (claimId: string) => string;
    /** Offer the remit's and each claim's PDF. Off in demo mode, which has none and must not fetch real ones (NS-14). */
    pdfs?: boolean;
}

const POPUP_BLOCKED = 'The browser blocked the new tab. Allow pop-ups for this site and try again.';

/**
 * Opens a short-lived document URL in a new tab, or says why there is none. The tab opens inside the click and is
 * pointed at the URL once it arrives: opened after the request, Safari treats it as a pop-up and blocks it silently.
 */
const openUrl = async (load: () => Promise<string | undefined>, setError: (message?: string) => void): Promise<void> => {
    setError(undefined);
    const target = window.open('', '_blank');
    if (!target) {
        setError(POPUP_BLOCKED);
        return;
    }
    try {
        const url = await load();
        if (url) {
            target.opener = null;
            target.location.href = url;
        } else {
            target.close();
            setError('There is no PDF for this remit.');
        }
    } catch {
        target.close();
        setError('The PDF could not be opened. Try again in a moment.');
    }
};

/** The stored 835, saved as the file a clearinghouse would have sent (crosswalk §2: client-side, `ERA_{eraId}.835`). */
const download835 = (remit: IRemit): void => {
    if (!remit.x12) {
        return;
    }
    const url = URL.createObjectURL(new Blob([remit.x12], { type: 'text/plain' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `ERA_${remit.displayId}.835`;
    // Attached, and revoked a moment later: Safari and Firefox drop a click on a detached link, or a URL revoked
    // before the download starts.
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10000);
};

const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
    <div>
        <dt>{label}</dt>
        <dd>{value === undefined || value === '' ? '—' : value}</dd>
    </div>
);

/**
 * The expanded remit (FE-163, designs 02 and 04): its payment facts, each claim it pays with its service lines, the
 * provider adjustments, and the balance line that ties them to the check.
 *
 * Only what the crosswalk maps is drawn. The design's PT RESP, ADJ TO TOTAL PREV PD, INTEREST, LATE FILING CHARGE
 * and NET lines have no source in it, so they are not here (FE-163 lists them as not built).
 */
export const RemitDetail: React.FC<IRemitDetailProps> = observer(({ store, remit, openLine, claimHref, pdfs = true }) => {
    const [error, setError] = React.useState<string>();
    const state = store.claims.get(remit.id);
    const claims = state?.claims;

    React.useEffect(() => {
        void store.loadClaims(remit);
    }, [store, remit]);
    // An EOB names no patient; each one comes from the claim its payment settles (crosswalk §2).
    React.useEffect(() => {
        if (remit.kind === 'EOB' && claims) {
            void store.loadClaimLinks(claims);
        }
    }, [store, remit.kind, claims]);

    // Parsed once per remit, not on every render: an ERA's provider adjustments come from its whole stored 835.
    const adjustments = React.useMemo(() => store.providerAdjustments(remit), [store, remit]);
    const paid = store.paidOf(remit);
    const counts = claims ? postedCount(claims) : undefined;
    // Unknown adjustments (an unreadable 835) are not zero: no balance line rather than a false "Not explained".
    const balance = claims && adjustments ? remitBalance(claims, adjustments, paid) : undefined;
    const isEra = remit.kind === 'ERA';

    const viewClaim = async (paymentId: string): Promise<void> => {
        // Opened before the lookup so the browser treats it as the click's own window, not a pop-up.
        const target = window.open('', '_blank');
        if (!target) {
            setError(POPUP_BLOCKED);
            return;
        }
        setError(undefined);
        const link = await store.claimLink(paymentId);
        if (link) {
            target.location.href = claimHref(link.id);
        } else {
            target.close();
            setError('The claim behind this payment could not be found.');
        }
    };

    return (
        <S.Detail data-testid="remit-detail">
            <S.MetaRow>
                <S.Meta>
                    {isEra ? (
                        <>
                            <Field label="Billing NPI" value={remit.npi} />
                            <Field label="EIN" value={remit.ein} />
                        </>
                    ) : (
                        <Field label="Payer on check" value={remit.payerName} />
                    )}
                    <Field label="Payment type" value={paymentTypeLabel(remit.paymentType)} />
                    <Field label="Payment number" value={remit.paymentNumber} />
                    {!isEra && <Field label="Paid date" value={day(remit.paidDate)} />}
                    <Field label={isEra ? 'Received' : 'Uploaded'} value={day(remit.created)} />
                    {isEra ? (
                        <Field
                            label="Clearinghouse"
                            value={store.clearinghouseName(remit.clearinghouseId) || remit.clearinghouseId}
                        />
                    ) : (
                        <Field label="Supporting document" value={remit.documentId} />
                    )}
                    {/* Linked, not "Reconciled": a link says which deposit the remit was matched to, not that the amounts
                        agree. The deposit's own reference needs the banking lookup Reconcile brings (crosswalk §2). */}
                    <Field
                        label="Bank transaction"
                        value={remit.plaidItemTransactionId ? 'Linked to a deposit' : 'Unreconciled'}
                    />
                </S.Meta>
                <S.Actions>
                    {pdfs && (isEra || remit.documentId) && (
                        <BaseButton
                            variant="outlined"
                            size="compact"
                            onClick={(): void => void openUrl(() => store.api.remitPdfUrl(remit), setError)}>
                            <Icon path={mdiFilePdfBox} size={0.7} />
                            <span>Download PDF</span>
                        </BaseButton>
                    )}
                    {isEra && remit.x12 && (
                        <BaseButton variant="outlined" size="compact" onClick={(): void => download835(remit)}>
                            <Icon path={mdiDownload} size={0.7} />
                            <span>Download 835 ERA</span>
                        </BaseButton>
                    )}
                </S.Actions>
            </S.MetaRow>

            {error && <Notice tone="attention">{error}</Notice>}

            <S.Section>
                <S.SectionHead>
                    <span>Claim remits</span>
                    <span>{counts ? `${counts.posted} of ${counts.total} posted` : ''}</span>
                </S.SectionHead>
                {!state || state.loading ? (
                    <S.Note role="status">
                        <Spinner size="14px" /> Loading the claims on this remit&hellip;
                    </S.Note>
                ) : state.error ? (
                    <S.NoticeSlot>
                        <Notice tone="attention">{state.error}</Notice>
                    </S.NoticeSlot>
                ) : !claims?.length ? (
                    // Measured 2026-10-08: some EOBs have no claim payments yet, so the whole check is unexplained, and the
                    // page says so rather than drawing an empty table under a balance line. An ERA without claims is not
                    // waiting for anything: one that carries only provider adjustments (a recoupment, interest) is final.
                    <S.Note>
                        {isEra
                            ? `This ERA has no claim lines.${adjustments?.length ? ' Its payment is made up of the provider adjustments below.' : ''}`
                            : `No claim payments recorded on this EOB yet, so none of its ${money(paid)} is attributed to a claim.`}
                    </S.Note>
                ) : (
                    claims.map(claim => (
                        <ClaimRemit
                            key={claim.key}
                            claim={claim}
                            isEra={isEra}
                            patient={
                                isEra
                                    ? claim.patient
                                    : claim.paymentId
                                    ? store.claimLinks.get(claim.paymentId)?.patient
                                    : undefined
                            }
                            onPdf={
                                pdfs && isEra && claim.itemId
                                    ? (): void => {
                                          const itemId = claim.itemId as string;
                                          void openUrl(() => store.api.eraClaimPdfUrl(itemId), setError);
                                      }
                                    : undefined
                            }
                            onViewClaim={
                                claim.posted && claim.paymentId
                                    ? (): void => void viewClaim(claim.paymentId as string)
                                    : undefined
                            }
                            openLine={openLine}
                        />
                    ))
                )}
            </S.Section>

            <S.Section>
                <S.SectionHead>
                    <span>Provider adjustments</span>
                    <span>
                        {adjustments
                            ? `${adjustments.length} ${adjustments.length === 1 ? 'adjustment' : 'adjustments'}`
                            : ''}
                    </span>
                </S.SectionHead>
                {!adjustments ? (
                    <S.Note>The stored 835 for this ERA could not be read, so its provider adjustments are not shown.</S.Note>
                ) : adjustments.length ? (
                    <S.Lines>
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Adjustment</th>
                                <th>{isEra ? 'Reference' : 'Identifier'}</th>
                                {isEra && <th>Provider</th>}
                                {isEra && <th>Fiscal period</th>}
                                <th className="num">Effect on payment</th>
                            </tr>
                        </thead>
                        <tbody>
                            {adjustments.map((adjustment, index) => (
                                <tr key={`${adjustment.code}-${index}`}>
                                    <td>
                                        <strong>{adjustment.code}</strong>
                                    </td>
                                    <td>{adjustment.label}</td>
                                    <td>{adjustment.reference || '—'}</td>
                                    {isEra && <td>{adjustment.provider || '—'}</td>}
                                    {isEra && <td>{day(adjustment.fiscalPeriod)}</td>}
                                    <td className="num">
                                        <S.Effect sign={Math.sign(adjustment.effect)}>{signedMoney(adjustment.effect)}</S.Effect>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </S.Lines>
                ) : (
                    <S.Note>None on this remit.</S.Note>
                )}
                {balance && (
                    <S.Balance data-testid="remit-balance">
                        <span>
                            <S.Label>Claim remits</S.Label>
                            {money(balance.claimRemits)}
                        </span>
                        <span>
                            <S.Label>Provider adj</S.Label>
                            <S.Effect sign={Math.sign(balance.providerAdjustments)}>
                                {signedMoney(balance.providerAdjustments)}
                            </S.Effect>
                        </span>
                        <span>
                            <S.Label>{isEra ? 'Paid (BPR02)' : 'Paid amount'}</S.Label>
                            <strong>{money(balance.paid)}</strong>
                        </span>
                        {balance.difference !== 0 && claims && claims.length > 0 && (
                            <span>
                                <S.Label>Not explained</S.Label>
                                <S.Effect sign={-1}>{money(balance.difference)}</S.Effect>
                            </span>
                        )}
                    </S.Balance>
                )}
            </S.Section>
        </S.Detail>
    );
});

interface IClaimRemitProps {
    claim: IRemitClaim;
    isEra: boolean;
    patient?: string;
    onPdf?: () => void;
    onViewClaim?: () => void;
    openLine: (line: IRemitLine, claim: IRemitClaim, patient?: string) => void;
}

const dash = (value: React.ReactNode): React.ReactNode => (value === undefined || value === '' ? '—' : value);

/** One claim on the remit: its identifiers, its service lines, its totals and its status. */
const ClaimRemit: React.FC<IClaimRemitProps> = ({ claim, isEra, patient, onPdf, onViewClaim, openLine }) => (
    <S.Claim data-testid="claim-remit">
        <S.ClaimHead>
            <span>
                <S.Label>Patient</S.Label>
                <strong>{dash(patient)}</strong>
            </span>
            <span>
                <S.Label>Subscriber</S.Label>
                {dash([claim.subscriber, claim.subscriberId].filter(Boolean).join(' · '))}
            </span>
            <span>
                <S.Label>PCN</S.Label>
                {dash(claim.pcn)}
            </span>
            <span>
                <S.Label>ICN</S.Label>
                {dash(claim.icn)}
            </span>
            <span>
                <S.Label>Group</S.Label>
                {dash(claim.groupId)}
            </span>
            <span>
                <S.Label>Contract</S.Label>
                {dash(claim.contract)}
            </span>
            <S.ClaimLinks>
                <S.Posted tone={claim.posted ? 'all' : 'none'}>{claim.posted ? 'Posted' : 'Unposted'}</S.Posted>
                {onPdf && (
                    <BaseButton variant="text" size="compact" onClick={onPdf}>
                        <Icon path={mdiFilePdfBox} size={0.7} />
                        <span>PDF</span>
                    </BaseButton>
                )}
                {onViewClaim && (
                    <BaseButton variant="text" size="compact" onClick={onViewClaim}>
                        <span>View Claim</span>
                        <Icon path={mdiOpenInNew} size={0.6} />
                    </BaseButton>
                )}
            </S.ClaimLinks>
        </S.ClaimHead>
        <S.Lines>
            <thead>
                <tr>
                    <th>Rend prov</th>
                    <th>Serv date</th>
                    <th>POS</th>
                    <th>NOS</th>
                    <th>Proc</th>
                    <th>Mods</th>
                    <th className="num">Billed</th>
                    <th className="num">Allowed</th>
                    <th className="num">Deduct</th>
                    <th className="num">Coins</th>
                    <th className="num">Prov pd</th>
                    <th>Grp/RC-amt</th>
                </tr>
            </thead>
            <tbody>
                {claim.lines.map(line => (
                    <tr
                        key={line.key}
                        className="line"
                        tabIndex={0}
                        title="Open this service line"
                        onClick={(): void => openLine(line, claim, patient)}
                        onKeyDown={(event): void => {
                            if (event.key === 'Enter') {
                                openLine(line, claim, patient);
                            }
                        }}>
                        <td>{dash(claim.renderingNpi)}</td>
                        <td>{serviceDays(line.fromDos, line.throughDos)}</td>
                        <td>{dash(line.placeOfService)}</td>
                        <td>{dash(line.units)}</td>
                        <td>{dash(line.procedureCode)}</td>
                        <td>{line.modifiers.length ? line.modifiers.join(' ') : '—'}</td>
                        <td className="num">{plain(line.billed)}</td>
                        <td className="num">{plain(line.allowed)}</td>
                        <td className="num">{plain(line.deductible)}</td>
                        <td className="num">{plain(line.coinsurance)}</td>
                        <td className="num">
                            <strong>{plain(line.paid)}</strong>
                        </td>
                        <td>
                            {line.adjustments.map((adjustment, index) => (
                                <S.Adjustment key={`${adjustment.group}-${adjustment.code}-${index}`}>
                                    <span>
                                        {adjustment.group}-{adjustment.code}
                                    </span>
                                    <span>{plain(adjustment.amount)}</span>
                                </S.Adjustment>
                            ))}
                        </td>
                    </tr>
                ))}
                <tr className="totals">
                    <td colSpan={6}>
                        <S.Label>Claim totals</S.Label>
                    </td>
                    <td className="num">{plain(claim.totals.billed)}</td>
                    <td className="num">{plain(claim.totals.allowed)}</td>
                    <td className="num">{plain(claim.totals.deductible)}</td>
                    <td className="num">{plain(claim.totals.coinsurance)}</td>
                    <td className="num">{plain(claim.totals.paid)}</td>
                    <td>{plain(claim.totals.adjusted)}</td>
                </tr>
            </tbody>
        </S.Lines>
        <S.ClaimFoot>
            <span>
                {/* An ERA prints CLP02; an EOB's status is words only (crosswalk §2). */}
                <S.Label>{isEra && claim.status.code ? `Status code ${claim.status.code}` : 'Status'}</S.Label>
                {claim.status.label}
            </span>
            {isEra && <span>{claim.crossoverCarrier ? `Crossover carrier: ${claim.crossoverCarrier}` : 'No crossover carrier'}</span>}
        </S.ClaimFoot>
    </S.Claim>
);
